import React, { useState, useEffect, useCallback, useRef } from 'react';
import { expenseApi } from '../lib/api';
import { useUnit } from '../contexts/UnitContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';

const inr = (v) => (v == null ? '—' : `₹${Number(v).toLocaleString('en-IN')}`);
const HR_MONTHS = ['Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'];
const MONTH_FULL = { Apr: 'April', May: 'May', Jun: 'June', Jul: 'July', Aug: 'August', Sep: 'September',
  Oct: 'October', Nov: 'November', Dec: 'December', Jan: 'January', Feb: 'February', Mar: 'March' };
function fyNow() {
  const d = new Date();
  const start = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return `${start}-${String(start + 1).slice(-2)}`;
}
const ymOf = (fy, m) => {
  const start = parseInt(fy.slice(0, 4), 10);
  const idx = HR_MONTHS.indexOf(m);
  const y = idx <= 8 ? start : start + 1;
  return `${y}-${String(idx <= 8 ? idx + 4 : idx - 8).padStart(2, '0')}`;
};

export default function Expense() {
  // The register is per plant. Without this every unit read and wrote the
  // same six rows, which is why Unit 1's sales appeared under Unit 3.
  const { activeUnit } = useUnit();
  const [data, setData] = useState(null);
  const [preview, setPreview] = useState(null);
  const [replace, setReplace] = useState({});
  const [busy, setBusy] = useState(false);
  const [fySel, setFySel] = useState(fyNow());
  const fileRef = useRef();
  // Typed-in figures, keyed by YYYY-MM. A month is only in here while it is
  // being edited, so an untouched row still shows exactly what is saved.
  const [edit, setEdit] = useState({});
  const [savingYm, setSavingYm] = useState('');

  // Months recorded before the register was per-plant belong to nobody. They
  // are shown apart from this unit's figures and claimed deliberately — only a
  // person knows which plant they describe, and guessing would attribute one
  // plant's sales to another.
  const claimUnassigned = async () => {
    if (!activeUnit) { toast.error('Pick a unit first'); return; }
    const list = (data?.unassigned || []).map((r) => r.ym);
    if (!window.confirm(
      `Assign ${list.length} unassigned month(s) to ${activeUnit}?\n\n`
      + `${list.join(', ')}\n\nMonths this unit already has are left alone.`)) return;
    try {
      const r = await expenseApi.claim({ unit: activeUnit, months: list });
      toast.success(`${r.data.claimed.length} month(s) assigned to ${activeUnit}`
        + (r.data.skipped.length ? ` · ${r.data.skipped.length} skipped (already filled)` : ''));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not assign'); }
  };

  // A plant switch leaves the previous read in flight; without this guard the
  // slower reply wins and the register shows another plant's months.
  const wanted = useRef(activeUnit);
  const load = useCallback(async () => {
    wanted.current = activeUnit;
    try {
      const r = await expenseApi.list(activeUnit);
      if (wanted.current === activeUnit) setData(r.data);
    }
    catch (e) { if (wanted.current === activeUnit) toast.error('Failed to load expense register'); }
  }, [activeUnit]);
  useEffect(() => { load(); }, [load]);

  const pick = async (f) => {
    if (!f) return;
    setBusy(true); setPreview(null); setReplace({});
    try {
      const fd = new FormData();
      fd.append('file', f);
      // The spreadsheet describes ONE plant: the one on screen.
      fd.append('unit', activeUnit || '');
      const r = await expenseApi.preview(fd);
      setPreview(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not read the Excel file'); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await expenseApi.commit({
        filename: preview.filename, unit: activeUnit || '', rows: preview.rows,
        replace: Object.keys(replace).filter((k) => replace[k]),
      });
      const d = r.data;
      toast.success(`Saved ${d.saved.length} month(s)` +
        (d.skipped_existing.length ? ` · ${d.skipped_existing.length} existing month(s) skipped (not confirmed)` : ''));
      setPreview(null); setReplace({});
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  // --- typing a month straight into the register -------------------------
  const setCell = (ym, field, v) =>
    setEdit((s) => ({ ...s, [ym]: { ...(s[ym] || {}), [field]: v } }));

  // What a row should show: the figure being typed if there is one, else what
  // is saved. Keeps an empty string empty rather than turning it into 0.
  const cellValue = (ym, field, row) => {
    const e = edit[ym];
    if (e && e[field] !== undefined) return e[field];
    return row ? String(row[field] ?? '') : '';
  };
  const rowDirty = (ym, row) => {
    const e = edit[ym];
    if (!e) return false;
    return ['sales', 'expense', 'salary'].some(
      (f) => e[f] !== undefined && String(e[f]) !== String(row ? (row[f] ?? '') : ''));
  };

  const saveMonth = async (ym, row) => {
    const sales = Number(cellValue(ym, 'sales', row));
    const expense = Number(cellValue(ym, 'expense', row));
    // Salary is optional: a blank stays blank rather than becoming a 0, because
    // 0% of sale is full marks on this KPI and must never stand in for "not
    // entered yet". The server applies the same rule.
    const salaryRaw = cellValue(ym, 'salary', row);
    const salary = salaryRaw === '' || salaryRaw === null || salaryRaw === undefined
      ? null : Number(salaryRaw);
    if (!sales || sales <= 0) { toast.error('Enter the Sale figure (more than 0) first.'); return; }
    if (!(expense >= 0)) { toast.error('Enter the Expense figure.'); return; }
    if (salary !== null && !(salary >= 0)) { toast.error('Salary cannot be negative.'); return; }
    setSavingYm(ym);
    try {
      const r = await expenseApi.saveMonth({ ym, unit: activeUnit || '', sales, expense, salary });
      toast.success(`${MONTH_FULL[r.data.month]} saved — ${r.data.pct}%`
        + (r.data.replaced ? ' (previous figures kept in history)' : ''));
      setEdit((s) => { const n = { ...s }; delete n[ym]; return n; });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSavingYm(''); }
  };

  const clearMonth = async (ym, label) => {
    if (!window.confirm(`Clear ${label}? The Sale and Expense figures for that month are removed.`)) return;
    setSavingYm(ym);
    try {
      await expenseApi.deleteMonth(ym, activeUnit || '');
      setEdit((s) => { const n = { ...s }; delete n[ym]; return n; });
      toast.success(`${label} cleared`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not clear'); }
    finally { setSavingYm(''); }
  };

  const numCell = 'w-full bg-transparent text-right outline-none px-1 py-0.5 rounded '
    + 'focus:bg-white focus:ring-2 focus:ring-[#C7D7FE] text-[#11182B]';

  return (
    <div className="flex h-screen app-shell">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header />
        <main className="flex-1 overflow-y-auto p-6 space-y-5">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-[-0.02em] text-[#172554]">Expense</h1>
            <p className="text-sm text-[#5A6377]">
              Monthly Sale, Salary &amp; Expense register. Both ratios are always calculated server-side — <b>Salary ÷ Sale × 100</b> and <b>Expense ÷ Sale × 100</b> — and a ratio typed into a sheet is ignored. This register automatically feeds the Plant Head KPIs <b>Sales Target</b> and <b>Personal Expenses Control</b>, and the Head HR KPI <b>Personnel Expenses</b>.
            </p>
          </div>

          {/* Upload */}
          <div className="bg-white border border-[#E6E9F0] rounded-xl p-5">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="font-medium text-[#11182B]">Upload Excel</h3>
              <input ref={fileRef} type="file" accept=".xlsx,.xlsm" className="hidden"
                onChange={(e) => pick(e.target.files?.[0])} data-testid="expense-file" />
              <Button size="sm" disabled={busy} onClick={() => fileRef.current?.click()}
                data-testid="expense-upload">Choose .xlsx file</Button>
              <span className="text-xs text-[#98A1B2]">Columns needed: Month | Sales | Expense · Salary optional (any % column is ignored — both ratios are recalculated)</span>
            </div>

            {preview && (
              <div className="mt-4 space-y-3">
                <div className="text-sm text-[#5A6377]">
                  <b>{preview.filename}</b> · {preview.valid_rows} valid row(s), {preview.invalid_rows} invalid ·
                  columns detected: month=✔ sales=✔ expense=✔
                  {preview.existing_months.length > 0 &&
                    <span className="text-[#8A6100]"> · {preview.existing_months.length} month(s) already exist — tick to replace</span>}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-[#EFF1F6] text-[#5A6377] text-xs uppercase">
                      <tr><th className="py-2 px-2 text-left">Month</th><th className="py-2 px-2 text-right">Sales</th><th className="py-2 px-2 text-right">Salary</th><th className="py-2 px-2 text-right">Salary % (calc)</th><th className="py-2 px-2 text-right">Expense</th><th className="py-2 px-2 text-right">Expense % (calc)</th><th className="py-2 px-2 text-left">Status</th></tr>
                    </thead>
                    <tbody>
                      {preview.rows.map((r, i) => (
                        <tr key={i} className={`border-b border-[#F2F4F8] ${!r.valid ? 'bg-[#FFF5F5]' : ''}`}>
                          <td className="py-1.5 px-2">{r.ym || r.month_raw}</td>
                          <td className="py-1.5 px-2 text-right">{inr(r.sales)}</td>
                          <td className="py-1.5 px-2 text-right">{inr(r.salary)}</td>
                          <td className="py-1.5 px-2 text-right font-semibold">{r.salary_pct != null ? `${r.salary_pct}%` : '—'}</td>
                          <td className="py-1.5 px-2 text-right">{inr(r.expense)}</td>
                          <td className="py-1.5 px-2 text-right font-semibold">{r.pct != null ? `${r.pct}%` : 'N/A'}</td>
                          <td className="py-1.5 px-2 text-xs">
                            {!r.valid ? <span className="text-[#C2151F]">{r.error}</span>
                              : r.exists ? (
                                <label className="flex items-center gap-1.5 text-[#8A6100] cursor-pointer">
                                  <input type="checkbox" checked={!!replace[r.ym]}
                                    onChange={(e) => setReplace((s) => ({ ...s, [r.ym]: e.target.checked }))} />
                                  exists (₹{Number(r.existing?.sales || 0).toLocaleString('en-IN')} / {r.existing?.pct}%) — replace?
                                </label>
                              ) : <span className="text-[#15A36E]">new</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={save} disabled={busy || preview.valid_rows === 0}
                    data-testid="expense-save">Confirm &amp; Save</Button>
                  <Button size="sm" variant="outline" onClick={() => { setPreview(null); setReplace({}); }}>Cancel</Button>
                </div>
              </div>
            )}
          </div>

          {/* Register — the notebook layout: Month | Sale | Expense | % with Apr..Mar + Avg */}
          <div className="bg-white border border-[#E6E9F0] rounded-xl p-5">
            {(() => {
              const fys = [...new Set([fyNow(), ...(data?.rows || []).map((r) => r.fy)])].sort().reverse();
              const byYm = Object.fromEntries((data?.rows || []).map((r) => [r.ym, r]));
              const months = HR_MONTHS.map((m) => ({ m, ym: ymOf(fySel, m), r: byYm[ymOf(fySel, m)] }));
              const filled = months.filter((x) => x.r);
              // Averaged over the months that actually carry a Salary, so a
              // month recorded before the column existed does not drag the
              // baseline down. This is the same rule the KPI engine applies.
              const salFilled = months.filter((x) => x.r && x.r.salary_pct != null);
              const salAvg = (f) => (salFilled.length
                ? salFilled.reduce((s2, x) => s2 + Number(x.r[f] || 0), 0) / salFilled.length : null);
              const avg = (f) => (filled.length ? filled.reduce((s2, x) => s2 + Number(x.r[f] || 0), 0) / filled.length : null);
              return (<>
                {(data?.unassigned || []).length > 0 && (
                  <div data-testid="expense-unassigned"
                    className="mb-3 rounded-lg border border-[#FDD08A] bg-[#FFF7E6] px-3 py-2.5 text-[12px] text-[#8A5A12]">
                    <b>{data.unassigned.length} month(s) are not assigned to any plant.</b>{' '}
                    They were recorded before this register was per-unit, so they are not
                    counted in {activeUnit || 'this unit'}'s figures above.
                    <div className="mt-1 text-[#11182B]">
                      {data.unassigned.map((r) => r.ym).join(', ')}
                    </div>
                    <button onClick={claimUnassigned} data-testid="expense-claim"
                      className="mt-1.5 text-[#2563EB] font-semibold hover:underline">
                      These belong to {activeUnit || 'this unit'} — assign them
                    </button>
                  </div>
                )}
                {/* Months recorded before the register was per-plant. They are
                    shown apart from this unit's figures rather than mixed in —
                    nothing in the data says which plant they describe, so they
                    are offered to be claimed rather than silently attributed. */}
                {(data?.unassigned || []).length > 0 && (
                  <div data-testid="expense-unassigned"
                    className="mb-3 flex items-start gap-2 text-[12px] text-[#B14A0E]
                               bg-[#FFF7E6] border border-[#FDD08A] rounded-md p-3">
                    <span className="font-bold">!</span>
                    <div>
                      <b>{data.unassigned.length} month(s) are not assigned to any plant.</b>{' '}
                      They were entered before this register separated units, so they are
                      not counted as {activeUnit || 'this unit'}&rsquo;s figures:{' '}
                      {data.unassigned.map((u) => u.ym).join(', ')}.
                      <div className="mt-1.5">
                        <Button size="sm" variant="outline" data-testid="expense-claim"
                          onClick={async () => {
                            if (!activeUnit) { toast.error('Pick a unit first'); return; }
                            if (!window.confirm(
                              `Assign these ${data.unassigned.length} month(s) to ${activeUnit}?\n\n`
                              + 'Only do this if these figures really belong to this plant.')) return;
                            try {
                              const r = await expenseApi.claim({ unit: activeUnit });
                              toast.success(`${r.data.claimed.length} month(s) assigned to ${activeUnit}`);
                              load();
                            } catch (e) { toast.error(e?.response?.data?.detail || 'Could not assign'); }
                          }}>
                          These belong to {activeUnit || 'this unit'}
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-3 mb-3">
                  <h3 className="font-medium text-[#11182B]">Monthly register</h3>
                  {activeUnit && (
                    <span className="text-[11px] font-semibold text-[#15307F] bg-[#E8EFFD] rounded px-2 py-0.5">
                      {activeUnit}
                    </span>
                  )}
                  <select value={fySel} onChange={(e) => setFySel(e.target.value)}
                    className="border border-[#E6E9F0] rounded-lg px-2 py-1 text-sm">
                    {fys.map((f) => <option key={f} value={f}>FY {f}</option>)}
                  </select>
                  {!data && <span className="text-[#98A1B2] text-sm">Loading…</span>}
                  <span className="text-xs text-[#5A6377] ml-auto">
                    Type Sale, Salary and Expense straight into the table, then press <b>Enter</b> or click <b>Save</b>.
                    Both percentages work themselves out.
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border border-[#C9CFDC]" style={{ borderCollapse: 'collapse' }}>
                    <thead>
                      <tr className="bg-[#EFF1F6] text-[#11182B] text-xs uppercase">
                        <th className="py-2 px-3 text-left border border-[#C9CFDC]">Month</th>
                        <th className="py-2 px-3 text-right border border-[#C9CFDC]">Sale</th>
                        <th className="py-2 px-3 text-right border border-[#C9CFDC]">Salary</th>
                        <th className="py-2 px-3 text-right border border-[#C9CFDC]">Salary % <span className="normal-case font-normal text-[#5A6377]">(Sal ÷ Sale × 100)</span></th>
                        <th className="py-2 px-3 text-right border border-[#C9CFDC]">Expense</th>
                        <th className="py-2 px-3 text-right border border-[#C9CFDC]">% <span className="normal-case font-normal text-[#5A6377]">(E ÷ S × 100)</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {months.map(({ m, ym, r }) => {
                        const s = cellValue(ym, 'sales', r);
                        const e = cellValue(ym, 'expense', r);
                        const dirty = rowDirty(ym, r);
                        // The percentage follows what is on screen, so the
                        // effect of a figure is visible before it is saved.
                        const livePct = (Number(s) > 0 && e !== '' && Number(e) >= 0)
                          ? (Number(e) / Number(s) * 100).toFixed(2) : null;
                        // The Personal Expenses ratio, shown live so the effect
                        // of a figure is visible before it is saved. A blank
                        // Salary stays blank — 0% of sale is full marks on that
                        // KPI and must never stand in for "not entered".
                        const w = cellValue(ym, 'salary', r);
                        const liveSalaryPct = (Number(s) > 0 && w !== '' && Number(w) >= 0)
                          ? (Number(w) / Number(s) * 100).toFixed(2) : null;
                        return (
                          <tr key={ym} className={dirty ? 'bg-[#FFFBEB]' : ''}>
                            <td className="py-1.5 px-3 border border-[#E6E9F0] font-medium text-[#11182B]">{MONTH_FULL[m]}
                              {r && (r.history || []).length > 0 && <span className="text-[10px] text-[#98A1B2]"> · {r.history.length} rev</span>}
                            </td>
                            <td className="py-1.5 px-2 border border-[#E6E9F0]">
                              <input type="number" min="0" step="any" value={s} placeholder="—"
                                data-testid={`expense-sales-${ym}`}
                                onChange={(ev) => setCell(ym, 'sales', ev.target.value)}
                                onKeyDown={(ev) => ev.key === 'Enter' && dirty && saveMonth(ym, r)}
                                className={numCell} />
                            </td>
                            <td className="py-1.5 px-2 border border-[#E6E9F0]">
                              <input type="number" min="0" step="any" value={w} placeholder="—"
                                data-testid={`expense-salary-${ym}`}
                                onChange={(ev) => setCell(ym, 'salary', ev.target.value)}
                                onKeyDown={(ev) => ev.key === 'Enter' && dirty && saveMonth(ym, r)}
                                className={numCell} />
                            </td>
                            <td className="py-1.5 px-3 border border-[#E6E9F0] text-right font-semibold text-[#172554]"
                              data-testid={`expense-salary-pct-${ym}`}>
                              <span className={dirty ? 'text-[#8A6100]' : ''}>
                                {liveSalaryPct != null ? `${liveSalaryPct}%` : '—'}
                              </span>
                            </td>
                            <td className="py-1.5 px-2 border border-[#E6E9F0]">
                              <input type="number" min="0" step="any" value={e} placeholder="—"
                                data-testid={`expense-expense-${ym}`}
                                onChange={(ev) => setCell(ym, 'expense', ev.target.value)}
                                onKeyDown={(ev) => ev.key === 'Enter' && dirty && saveMonth(ym, r)}
                                className={numCell} />
                            </td>
                            <td className="py-1.5 px-3 border border-[#E6E9F0] text-right font-semibold text-[#172554]">
                              <div className="flex items-center justify-end gap-2">
                                <span className={dirty ? 'text-[#8A6100]' : ''}>
                                  {livePct != null ? `${livePct}%` : '—'}
                                </span>
                                {dirty ? (
                                  <button onClick={() => saveMonth(ym, r)} disabled={savingYm === ym}
                                    data-testid={`expense-save-${ym}`}
                                    className="text-[11px] font-semibold bg-[#15A36E] text-white rounded px-2 py-0.5 hover:bg-[#12855A] disabled:opacity-60">
                                    {savingYm === ym ? 'Saving…' : 'Save'}
                                  </button>
                                ) : r ? (
                                  <button onClick={() => clearMonth(ym, MONTH_FULL[m])} disabled={savingYm === ym}
                                    title="Clear this month" data-testid={`expense-clear-${ym}`}
                                    className="text-[11px] text-[#98A1B2] hover:text-[#C2151F] font-normal">clear</button>
                                ) : <span className="w-8" />}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                      <tr className="bg-[#F7F8FB] font-bold text-[#172554]">
                        <td className="py-2 px-3 border border-[#C9CFDC]">Avg.</td>
                        <td className="py-2 px-3 border border-[#C9CFDC] text-right">{filled.length ? inr(Math.round(avg('sales'))) : '—'}</td>
                        <td className="py-2 px-3 border border-[#C9CFDC] text-right">{salFilled.length ? inr(Math.round(salAvg('salary'))) : '—'}</td>
                        <td className="py-2 px-3 border border-[#C9CFDC] text-right" data-testid="expense-avg-salary-pct">
                          {salFilled.length ? `${salAvg('salary_pct').toFixed(2)}%` : '—'}
                        </td>
                        <td className="py-2 px-3 border border-[#C9CFDC] text-right">{filled.length ? inr(Math.round(avg('expense'))) : '—'}</td>
                        <td className="py-2 px-3 border border-[#C9CFDC] text-right">{filled.length ? `${avg('pct').toFixed(2)}%` : '—'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-[11px] text-[#98A1B2] mt-2">
                  Avg. row = average of the {filled.length || 0} filled month(s); the Salary columns average the {salFilled.length || 0} month(s) that carry a Salary. Sales Target compares each month against LAST FY's Avg. Sale; Personal Expenses Control against LAST FY's Avg. Salary % where one exists, otherwise Avg. Expense %.
                </p>
              </>);
            })()}
            <p className="text-[11px] text-[#98A1B2] mt-3">
              KPI links: Sales Target = sales increase % vs last FY's monthly average (5%=1 … 20%=4). <b>Personal Expenses Control</b> = reduction in points vs last FY's monthly average (0.5=1 … 2+=4), measured on the <b>Salary %</b> once Salary is filled for this month and for last FY, and on the Expense % until then — so months recorded before the Salary column existed score exactly as they always did. Personnel Expenses (Head HR) = reduction in the expense figure itself vs last FY's monthly average (2%=1, 3%=2, 4%=3, 5%+=4). Enter last FY's months too, or these KPIs stay unscored (never guessed).
            </p>
          </div>
        </main>
      </div>
    </div>
  );
}
